/**
 * /products — Products CRUD page (Story 7.4 UI).
 *
 * Surface:
 *   - Paginated list with tier + active filters.
 *   - Price formatted as R$ (Brazilian Real, pt-BR).
 *   - Tier rendered as a color-coded badge (basic=blue, premium=purple).
 *   - "Novo produto" button → modal form (create).
 *   - Row actions: edit (opens modal), deactivate (DELETE → soft).
 *   - Successful mutations emit toasts; 409 PRODUCT_IN_USE / EXTERNAL_ID_TAKEN
 *     are translated into friendly messages.
 *
 * Backend was shipped in commit e703a01 (Wave 5, Dev B).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDate } from '@/lib/format-date';
import {
  ProductForm,
  DEFAULT_PRODUCT_FORM,
  type ProductFormValue,
  type ProductTier,
} from '@/components/products/ProductForm';

export interface ProductDto {
  id: string;
  externalId: string;
  name: string;
  description: string | null;
  tier: ProductTier;
  price: number;
  currency: string;
  paymentLink: string | null;
  discountLink: string | null;
  accessLink: string | null;
  specialVideoLink: string | null;
  discountPercent: number | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  data: ProductDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface Filters {
  tier: ProductTier | '';
  active: '' | 'true' | 'false';
}

const TIER_BADGE: Record<ProductTier, string> = {
  basic: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  premium: 'bg-purple-500/20 text-purple-700 dark:text-purple-300',
};

function TierBadge({ tier }: { tier: ProductTier }) {
  return (
    <span
      data-testid={`tier-badge-${tier}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${TIER_BADGE[tier]}`}
    >
      {tier}
    </span>
  );
}

/** Format a Number as Brazilian Real currency. */
export function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: { code?: string; message?: string; campaigns?: Array<{ name: string }> };
    };
    const code = body?.error?.code;
    if (code === 'EXTERNAL_ID_TAKEN') {
      return 'Já existe um produto com esse External ID.';
    }
    if (code === 'PRODUCT_IN_USE') {
      const names = Array.isArray(body.error?.campaigns)
        ? body.error.campaigns
            .map((c) => c.name)
            .filter(Boolean)
            .join(', ')
        : '';
      return names
        ? `Produto em uso pelas campanhas ativas: ${names}.`
        : 'Produto em uso por campanhas ativas.';
    }
    if (code === 'PRODUCT_NOT_FOUND') {
      return 'Produto não encontrado.';
    }
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toFormValue(p: ProductDto): ProductFormValue {
  return {
    externalId: p.externalId,
    name: p.name,
    description: p.description,
    tier: p.tier,
    price: p.price,
    paymentLink: p.paymentLink,
    discountLink: p.discountLink,
    accessLink: p.accessLink,
    specialVideoLink: p.specialVideoLink,
    discountPercent: p.discountPercent,
    active: p.active,
  };
}

/** Map the create-form value to the wire body (strip `active` — server default). */
function toCreateBody(v: ProductFormValue): Record<string, unknown> {
  return {
    externalId: v.externalId,
    name: v.name,
    description: v.description,
    tier: v.tier,
    price: v.price,
    paymentLink: v.paymentLink,
    discountLink: v.discountLink,
    accessLink: v.accessLink,
    specialVideoLink: v.specialVideoLink,
    discountPercent: v.discountPercent,
  };
}

/** Map the edit-form value to the wire body — no externalId (locked server-side). */
function toUpdateBody(v: ProductFormValue): Record<string, unknown> {
  return {
    name: v.name,
    description: v.description,
    tier: v.tier,
    price: v.price,
    paymentLink: v.paymentLink,
    discountLink: v.discountLink,
    accessLink: v.accessLink,
    specialVideoLink: v.specialVideoLink,
    discountPercent: v.discountPercent,
    active: v.active,
  };
}

export function ProductsPage() {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<Filters>({ tier: '', active: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ProductDto | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin', 'products', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', '1');
      params.set('pageSize', '50');
      if (filters.tier) params.set('tier', filters.tier);
      if (filters.active) params.set('active', filters.active);
      return apiClient.get<ListResponse>(`/admin/products?${params.toString()}`);
    },
  });

  const createMutation = useMutation({
    mutationFn: (v: ProductFormValue) =>
      apiClient.post<ProductDto>('/admin/products', toCreateBody(v)),
    onSuccess: () => {
      toast.success('Produto criado.');
      setCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao criar produto.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ProductFormValue }) =>
      apiClient.put<ProductDto>(`/admin/products/${id}`, toUpdateBody(body)),
    onSuccess: () => {
      toast.success('Produto atualizado.');
      setEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao atualizar produto.'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<ProductDto>(`/admin/products/${id}`),
    onSuccess: () => {
      toast.success('Produto desativado.');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao desativar produto.'));
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Produtos</h1>
          <p className="text-sm text-muted-foreground">
            Catálogo de produtos (básico/premium) com links de pagamento e descontos.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1" aria-hidden />
          Novo produto
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <select
              aria-label="Filtro tier"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.tier}
              onChange={(e) => setFilters({ ...filters, tier: e.target.value as ProductTier | '' })}
            >
              <option value="">Todos os tiers</option>
              <option value="basic">basic</option>
              <option value="premium">premium</option>
            </select>
            <select
              aria-label="Filtro active"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.active}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  active: e.target.value as '' | 'true' | 'false',
                })
              }
            >
              <option value="">Ativos + inativos</option>
              <option value="true">Apenas ativos</option>
              <option value="false">Apenas inativos</option>
            </select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar produtos.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Tier</th>
                    <th className="px-4 py-2">Preço</th>
                    <th className="px-4 py-2">Ativo</th>
                    <th className="px-4 py-2">Criado em</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((p) => (
                      <tr key={p.id} className="border-b last:border-b-0">
                        <td className="max-w-[12rem] px-4 py-2">
                          <div className="truncate font-medium">{p.name}</div>
                          <div className="break-all font-mono text-xs text-muted-foreground">
                            {p.externalId}
                          </div>
                        </td>
                        <td className="px-4 py-2">
                          <TierBadge tier={p.tier} />
                        </td>
                        <td className="px-4 py-2">{formatBRL(p.price)}</td>
                        <td className="px-4 py-2">{p.active ? 'Sim' : 'Não'}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">
                          {formatDate(p.createdAt)}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(p)}
                              aria-label={`Editar ${p.name}`}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!p.active || deactivateMutation.isPending}
                              onClick={() => deactivateMutation.mutate(p.id)}
                              aria-label={`Desativar ${p.name}`}
                            >
                              <Power aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum produto encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo produto</DialogTitle>
            <DialogDescription>
              Preencha os dados do produto para adicioná-lo ao catálogo.
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            mode="create"
            initial={DEFAULT_PRODUCT_FORM}
            submitting={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar produto</DialogTitle>
            <DialogDescription>
              O External ID é travado após a criação (chave de reconciliação).
            </DialogDescription>
          </DialogHeader>
          {editTarget ? (
            <ProductForm
              mode="edit"
              submitting={updateMutation.isPending}
              initial={toFormValue(editTarget)}
              onCancel={() => setEditTarget(null)}
              onSubmit={(next) => updateMutation.mutate({ id: editTarget.id, body: next })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProductsPage;
